import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  CoordinateViewModel,
  CropHealthViewModel,
  KeyValueModel,
  NoteViewModel,
  SatelliteProblematicSpotView,
  SatelliteViewModel,
} from '@/app/proxy/proxies';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import mapboxgl, { LngLat, LngLatBoundsLike } from 'mapbox-gl';
import { MapBoxService } from '@/app/core/services/mapbox.service';
import { MixpanelTrackerService } from '@/app/core/analytics';
import { PlantationService } from '@/app/core/services/plantation.service';
import { CropHealthMapCalendarComponent } from './crop-health-map-calendar/crop-health-map-calendar.component';
import { CropHealthMapControlsComponent } from './crop-health-map-controls/crop-health-map-controls.component';
import { CropHealthMapFooterComponent, CropHealthMapType } from './crop-health-map-footer/crop-health-map-footer.component';
import { CropHealthMapHeaderComponent } from './crop-health-map-header/crop-health-map-header.component';
import { CropHealthMapMessageOverlayComponent, MapMessageTypeEnum } from './crop-health-map-message-overlay/crop-health-map-message-overlay.component';
import { CropHealthMode, CropHealthService, SelectedCropHealthDay } from '../../../services/crop-health.service';
import { SharedModule } from '@/app/shared/shared.module';
import { Subject, takeUntil } from 'rxjs';
import { ServicesMapViewedAction } from '@/app/core/analytics/mixpanel-events.types';
import { NotePinBatchItem, NotePinLayerService } from './note-pin-layer.service';
@Component({
  selector: 'app-crop-health-map',
  imports: [
    SharedModule,
    CropHealthMapCalendarComponent,
    CropHealthMapFooterComponent,
    CropHealthMapHeaderComponent,
    CropHealthMapMessageOverlayComponent,
    CropHealthMapControlsComponent,
  ],
  templateUrl: './crop-health-map.component.html',
  styleUrl: './crop-health-map.component.less',
})
export class CropHealthMapComponent implements OnChanges, OnDestroy {
  @Input() loading = false;
  @Input() noteLoading = false;
  // null => distribution card hasn't computed the "issue" state for this day yet
  @Input() noAnalysisIssueState: { date: string; visible: boolean } | null = null;
  @Input() selectedMapType: CropHealthMapType;
  @Input() cropHealthData: CropHealthViewModel;
  @Output() readonly fullScreenEvent = new EventEmitter();
  @Output() readonly mapReadyEvent = new EventEmitter();
  @Output() readonly selectDateEvent = new EventEmitter();
  @Output() readonly loadMoreEvent = new EventEmitter();
  @Output() readonly seeProblematicSpotEvent = new EventEmitter();
  @Output() readonly viewNoteEvent = new EventEmitter<NoteViewModel>();

  @ViewChild(CropHealthMapHeaderComponent, { static: false })
  cropHealthMapHeaderComponent: CropHealthMapHeaderComponent;
  @ViewChild(CropHealthMapFooterComponent, { static: false })
  cropHealthMapFooterComponent: CropHealthMapFooterComponent;
  @ViewChild(CropHealthMapCalendarComponent, { static: false })
  cropHealthMapCalendarComponent: CropHealthMapCalendarComponent;

  selectedDay: SatelliteViewModel;
  notes: NoteViewModel[] = [];
  problematicSpotStatuses: KeyValueModel[] = [];

  fullScreenMode = false;
  mapInstance: mapboxgl.Map;

  sourceId = 'rasterSourceId';
  layerId = 'rasterLayerId';

  problematicSpot: SatelliteProblematicSpotView;
  draw: MapboxDraw;

  controlMapZoomEnabled = true;

  mode: CropHealthMode;
  mapOverlayMessage: MapMessageTypeEnum;

  private destroy$ = new Subject<void>();
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private lastServicesMapViewedEventKey: string | null = null;
  private pendingServicesMapViewedEvent: SelectedCropHealthDay | null = null;
  private pendingServicesMapViewedAction: ServicesMapViewedAction | null = null;
  private pendingServicesMapViewedNotesVisibleOverride: boolean | null = null;
  private pendingServicesMapViewedFullScreenOverride: boolean | null = null;
  private notesLoadedOnce = false;
  // Mixpanel: keep context to log action-based "Services Map Viewed" events safely.
  private lastSelectedCropHealthDayEvent: SelectedCropHealthDay | null = null;
  private lastSelectedPlantationIdForSelectedDay: string | null = null;
  private nextServicesMapViewedAction: ServicesMapViewedAction | null = null;
  private nextServicesMapViewedDateSelectionPlace: string | null = null;
  private nextServicesMapViewedMapSelectionPlace: string | null = null;
  // Mixpanel: prevent a duplicate "base" event right after an action-based event
  // (e.g. fullscreen triggers a data refresh that re-emits map viewed without action).
  private suppressNextNonActionServicesMapViewed = false;
  private suppressNextNonActionServicesMapViewedKey: string | null = null;
  // Mixpanel: prevent losing SERVICES_MAP_VIEWED when distribution state never resolves.
  private noAnalysisIssueFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private noAnalysisIssueFallbackForDate: string | null = null;
  private plantationFromSelection: PlantationService['selectedPlantation'] = null;
  private dayWaitingForPlantation: SelectedCropHealthDay | null = null;

  private flushPendingServicesMapViewedEventIfReady(): void {
    // Mixpanel: ensure SERVICES_MAP_VIEWED is tracked with accurate "notes" property.
    if (!this.pendingServicesMapViewedEvent) {
      return;
    }

    if (this.noteLoading !== false || !this.notesLoadedOnce) {
      return;
    }

    if (this.noAnalysisIssueState === null) {
      const pendingDate = this.pendingServicesMapViewedEvent?.day?.imageDate?.toFormat?.('yyyy-MM-dd') ?? null;
      // Mixpanel: if distribution card never emits, flush after fallback timeout.
      if (!pendingDate || this.noAnalysisIssueFallbackForDate !== pendingDate) {
        return;
      }
    }

    const pending = this.pendingServicesMapViewedEvent;
    const pendingAction = this.pendingServicesMapViewedAction ?? this.nextServicesMapViewedAction;
    const pendingNotesVisibleOverride = this.pendingServicesMapViewedNotesVisibleOverride;
    const pendingFullScreenOverride = this.pendingServicesMapViewedFullScreenOverride;
    this.pendingServicesMapViewedEvent = null;
    this.pendingServicesMapViewedAction = null;
    this.pendingServicesMapViewedNotesVisibleOverride = null;
    this.pendingServicesMapViewedFullScreenOverride = null;
    this.trackServicesMapViewed(
      pending,
      pendingAction,
      pendingNotesVisibleOverride,
      pendingFullScreenOverride
    );
  }

  // Mixpanel: if distribution card doesn't compute issue state in time, don't drop the event.
  private scheduleNoAnalysisIssueFallback(date: string): void {
    if (this.noAnalysisIssueFallbackForDate === date) {
      return;
    }
    if (this.noAnalysisIssueFallbackTimer) {
      clearTimeout(this.noAnalysisIssueFallbackTimer);
      this.noAnalysisIssueFallbackTimer = null;
    }
    this.noAnalysisIssueFallbackForDate = date;
    this.noAnalysisIssueFallbackTimer = setTimeout(() => {
      this.flushPendingServicesMapViewedEventIfReady();
    }, 1500);
  }

  private getHasAnyPremiumPlan(): boolean {
    // Requirement: if user has at least one premium plantation anywhere, send true
    const hasPremiumInLandList =
      this.plantationService.landList?.some((land) =>
        land.plantations?.some((p) => !!(p as any)?.isPremium)
      ) ?? false;

    return hasPremiumInLandList || !!this.plantationService.selectedPlantation?.isPremium;
  }

  constructor(
    private mapboxService: MapBoxService,
    private mixpanelTracker: MixpanelTrackerService,
    private plantationService: PlantationService,
    public cropHealthService: CropHealthService,
    private notePinLayer: NotePinLayerService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['noteLoading']) {
      this.flushPendingServicesMapViewedEventIfReady();
    }

    if (changes?.['noAnalysisIssueState']) {
      // Mixpanel: state arrived, cancel fallback timer if it targets same date.
      if (
        this.noAnalysisIssueFallbackTimer &&
        this.noAnalysisIssueFallbackForDate &&
        this.noAnalysisIssueState?.date === this.noAnalysisIssueFallbackForDate
      ) {
        clearTimeout(this.noAnalysisIssueFallbackTimer);
        this.noAnalysisIssueFallbackTimer = null;
        this.noAnalysisIssueFallbackForDate = null;
      }
      this.flushPendingServicesMapViewedEventIfReady();
    }

    // Mixpanel: attribute the next "map viewed" to map type change.
    if (changes?.['selectedMapType'] && !changes['selectedMapType']?.firstChange) {
      this.nextServicesMapViewedAction = 'Map type change';
    }

    if (changes?.['cropHealthData']) {
      if (!(this.cropHealthData?.mapList?.length > 0)) {
        // when no cropHealth data for plantation is loaded, it means the data is getting prepared.
        // so it needs to show a message on the map.
        // in addition, a DRAW should be added to show the boundary of the plantation
        this.cleanRasterLayers();

        if (this.loading || !this.plantationService.selectedPlantation?.boundaries) {
          return;
        }
        this.cropHealthService.setMapOverlayMessage(MapMessageTypeEnum.imagePreparing);
        this.mapboxService.addDrawablePolygon(
          this.plantationService.selectedPlantation?.boundaries,
          this.mapInstance
        );
        this.fitCameraToBoundaries(this.plantationService.selectedPlantation?.boundaries as number[][]);
      } else {
        this.cropHealthService.setMapOverlayMessage(undefined);
      }
    }
  }

  ngOnInit(): void {
    this.mapboxService.buildMap().subscribe((res) => {
      // The shared map can already be torn down by another view before 'load' fires.
      if (!res) {
        return;
      }

      this.mapInstance = res;
      this.mapReadyEvent.emit(res);

      // Symbol-layer note pins (replaces DOM mapboxgl.Marker); pin size follows zoom via layer icon-size.
      this.notePinLayer.configure({
        getMap: () => this.mapInstance,
        getBeforeLabelLayerId: () =>
          this.mapInstance?.getStyle()?.layers?.find((layer) => layer.type === 'symbol')?.id,
      });
      this.notePinLayer.setNoteClickHandler((noteId) => {
        const note = this.notes.find((n) => n.id === noteId);
        if (!note) {
          return;
        }
        this.notePinLayer.hideBatch();
        this.viewNoteEvent.emit(note);
      });

      this.initSubscriptions();
    });
  }

  initSubscriptions() {
    this.cropHealthService.noteLookups$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.problematicSpotStatuses = res.problematicSpotStatuses ?? [];
    });

    this.cropHealthService.mode$.pipe(takeUntil(this.destroy$)).subscribe(mode => {
      this.mode = mode;
      this.scheduleMapResize();
    });

    this.cropHealthService.notes$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.notes = res;
      this.notesLoadedOnce = true;
      this.flushPendingServicesMapViewedEventIfReady();
    });

    this.cropHealthService.showNotePins$.pipe(takeUntil(this.destroy$)).subscribe(show => {
      this.showNotesPins(show);
    });

    this.cropHealthService.mapOverlayMessage$.pipe(takeUntil(this.destroy$)).subscribe(message => {
      this.mapOverlayMessage = message
    });

    this.cropHealthService.selectedDay$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.changeDay(res);
    });

    this.plantationService.selectedPlantation$.pipe(takeUntil(this.destroy$)).subscribe((plantation) => {
      this.plantationFromSelection = plantation;
      if (!plantation || !this.dayWaitingForPlantation) {
        return;
      }
      const waiting = this.dayWaitingForPlantation;
      this.dayWaitingForPlantation = null;
      this.changeDay(waiting);
    });
  }

  private scheduleMapResize(): void {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.resizeTimer = setTimeout(() => {
      this.resizeTimer = null;
      // Another view's buildMap() can remove() this instance while the timer is pending.
      if (this.mapInstance && this.mapInstance === this.mapboxService.map) {
        this.mapInstance.resize();
      }
    }, 500);
  }

  ngOnDestroy(): void {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
    if (this.noAnalysisIssueFallbackTimer) {
      clearTimeout(this.noAnalysisIssueFallbackTimer);
      this.noAnalysisIssueFallbackTimer = null;
      this.noAnalysisIssueFallbackForDate = null;
    }
    // Pin layers/sources are not owned by MapBoxService.resetMap().
    this.notePinLayer.dispose();
    this.mapboxService.removeProblematicSpots(this.mapInstance);
    // `mapboxService.map` is shared: buildMap() disposes the previous instance itself.
    this.mapboxService.resetMap();
    this.mapboxService.draw = null;
    this.mapboxService.deleteDrawing(this.mapInstance);

    this.destroy$.next();
    this.destroy$.complete();
  }

  changeDay(event: SelectedCropHealthDay) {
    // Mixpanel: attribute the next "map viewed" to date change (calendar/swipe).
    const previousIsoDate = this.selectedDay?.imageDate?.toISODate?.() ?? null;
    const nextIsoDate = event?.day?.imageDate?.toISODate?.() ?? null;
    const currentPlantationId = this.plantationService.selectedPlantation?.id ?? null;
    // IMPORTANT: Do not mark initial page load as "Date change".
    if (
      !this.nextServicesMapViewedAction &&
      // Mixpanel: only set Date change within same plantation.
      this.lastSelectedPlantationIdForSelectedDay &&
      currentPlantationId &&
      this.lastSelectedPlantationIdForSelectedDay === currentPlantationId &&
      previousIsoDate &&
      nextIsoDate &&
      previousIsoDate !== nextIsoDate
    ) {
      this.nextServicesMapViewedAction = 'Date change';
    }

    this.lastSelectedCropHealthDayEvent = event ?? null;
    this.lastSelectedPlantationIdForSelectedDay = currentPlantationId;
    this.selectedDay = event?.day;
    this.problematicSpot = null;
    this.mapboxService.deleteDrawing(this.mapInstance);
    this.mapboxService.removeProblematicSpots(this.mapInstance);

    if (!event?.day) {
      return;
    }

    const plantation = this.plantationService.selectedPlantation ?? this.plantationFromSelection;
    if (!plantation) {
      this.dayWaitingForPlantation = event;
      return;
    }
    this.dayWaitingForPlantation = null;

    this.cleanRasterLayers();
    if (event.day.isCloudly) {
      // show only polygon of the plantation if the day is cloudy (no image is provided)
      this.mapboxService.addDrawablePolygon(plantation.boundaries, this.mapInstance);
      this.trackServicesMapViewed(event, this.nextServicesMapViewedAction);
    } else {
      let imageCoordinates;
      if (plantation.boundaries) {
        imageCoordinates = this.getImageBoundsFromPolygon(plantation.boundaries as [number, number][]);

        // zoom the plantation boundaries
        const bounds = [
          imageCoordinates[3], // bottom-left
          imageCoordinates[1], // top-right
        ] as LngLatBoundsLike;
        const options = {
          padding: { top: 20, bottom: 170, left: 20, right: 20 },
          duration: 1000,
        };
        this.mapInstance?.fitBounds(bounds, options);
      }

      if (imageCoordinates) {
        // selectedDay$ also emits while the view is being destroyed, when the map is gone.
        if (!this.mapInstance) {
          return;
        }

        this.mapInstance.addSource(this.sourceId, {
          type: 'image',
          url: event.day.imageUrl,
          coordinates: imageCoordinates,
        });
        this.mapInstance.addLayer({
          id: this.layerId,
          type: 'raster',
          source: this.sourceId,
          paint: {
            'raster-opacity': 1,
            'raster-resampling': 'nearest',
          },
        });
        // Keep pins above NDVI raster; labels stay above pins (see NotePinLayerService.ensureStackOrder).
        this.notePinLayer.ensureStackOrder(this.layerId);
        this.trackServicesMapViewed(event, this.nextServicesMapViewedAction);

        // show plygon of the problematic spot if map type is RISK
        if (this.selectedMapType.key == 'risk' && event.problematicSpots?.length > 0) {
          const spotPolygons: number[][][] = [];
          for (const problematicSpot of event.problematicSpots) {
            const boundaries = [];
            for (const item of problematicSpot.boundaries) {
              boundaries.push([item.longitude, item.latitude]);
            }
            spotPolygons.push(boundaries);
          }
          this.mapboxService.drawProblematicSpots(spotPolygons, this.mapInstance);
        }
      }
    }
  }

  // Mixpanel: single place to build & send Services Map Viewed
  private trackServicesMapViewed(
    event: SelectedCropHealthDay | null,
    action?: ServicesMapViewedAction | null,
    notesVisibleOverride?: boolean | null,
    fullScreenModeOverride?: boolean | null,
  ): void {
    if (!this.plantationService.selectedPlantation || !event?.day?.imageDate) {
      return;
    }

    // Mixpanel: "notes" should reflect whether notes are actually shown to the user.
    // Wait until notes are loaded at least once so "notes" property is accurate.
    const currentDate = event.day.imageDate.toFormat('yyyy-MM-dd');
    const issueStateForThisDay =
      this.noAnalysisIssueState && this.noAnalysisIssueState.date === currentDate
        ? this.noAnalysisIssueState
        : this.noAnalysisIssueFallbackForDate === currentDate
          ? { date: currentDate, visible: false }
          : null;

    if (this.noteLoading !== false || !this.notesLoadedOnce || issueStateForThisDay === null) {
      if (issueStateForThisDay === null) {
        this.scheduleNoAnalysisIssueFallback(currentDate);
      }
      this.pendingServicesMapViewedEvent = event;
      this.pendingServicesMapViewedAction = action ?? null;
      this.pendingServicesMapViewedNotesVisibleOverride = notesVisibleOverride ?? null;
      this.pendingServicesMapViewedFullScreenOverride = fullScreenModeOverride ?? null;
      return;
    }
    const hasNotes = this.notes.length > 0;
    const notesVisible = notesVisibleOverride ?? !!this.cropHealthService.showNotePins;
    const fullScreenModeForKey = fullScreenModeOverride ?? this.fullScreenMode;
    const notesShownToUser = hasNotes && notesVisible;

    const nonActionContextKey = `${this.plantationService.selectedPlantation.id}-${this.selectedMapType?.key}-${event.day.imageDate.toISODate()}-${fullScreenModeForKey ? 'fs_on' : 'fs_off'}-${notesVisible ? 'notes_on' : 'notes_off'}`;
    if (!action && this.suppressNextNonActionServicesMapViewed) {
      // Only suppress the immediate duplicate that matches the same context.
      // This avoids accidentally suppressing a legitimate view (e.g. plantation change).
      if (this.suppressNextNonActionServicesMapViewedKey === nonActionContextKey) {
        this.suppressNextNonActionServicesMapViewed = false;
        this.suppressNextNonActionServicesMapViewedKey = null;
        return;
      }
      this.suppressNextNonActionServicesMapViewed = false;
      this.suppressNextNonActionServicesMapViewedKey = null;
    }

    // Dedupe key must include toggle states, otherwise toggling on/off won't emit twice.
    const eventKey = `${this.plantationService.selectedPlantation.id}-${this.selectedMapType?.key}-${event.day.imageDate.toISODate()}-${action ?? 'no_action'}-${fullScreenModeForKey ? 'fs_on' : 'fs_off'}-${notesVisible ? 'notes_on' : 'notes_off'}`;
    if (this.lastServicesMapViewedEventKey === eventKey) {
      return;
    }
    this.lastServicesMapViewedEventKey = eventKey;

    const land = this.plantationService.landList?.find((item) =>
      item.plantations?.some((plantation) => plantation.id === this.plantationService.selectedPlantation.id)
    );
    const enterpriseIdFromLand = (land as any)?.enterpriseId as string | undefined;
    const enterpriseId = enterpriseIdFromLand || this.plantationService.enterpriseId;
    const enterprise = this.plantationService.originalResult?.find((item) => item.id === enterpriseId);

    // Mixpanel: analytics-only derivation for distribution state (no UI/flow impact).
    const isPreparingDistribution = !(this.cropHealthData?.mapList?.length > 0);
    const hasAnyDistributionDataForDay = (() => {
      const distributions = (event.day as any)?.mapDistributions as any[] | undefined;
      if (!distributions?.length) return false;

      // 0: NdviRelativeDistribution, 1: NdviAbsoluteDistribution, 2: InspectionDistribution, 3: NdmiDistribution
      const relevantTypes: number[] =
        this.selectedMapType?.key === 'ndmi'
          ? [3]
          : this.selectedMapType?.key === 'inspection'
            ? [2]
            : this.selectedMapType?.key === 'ndvi' || this.selectedMapType?.key === 'risk'
              ? [0, 1]
              : [];

      if (relevantTypes.length === 0) return false;

      return distributions
        .filter((d) => relevantTypes.includes(d?.mapDistributionType))
        .some((d) => (d?.mapDistributionData?.length ?? 0) > 0);
    })();

    this.mixpanelTracker.trackServicesMapViewed({
      action: action ?? undefined,
      'full screen': fullScreenModeForKey,
      notes: notesShownToUser,
      'cloudy weather': !!event.day.isCloudly,
      date: currentDate,
      // Mixpanel: tri-state, does not affect UI flow.
      // - undefined: cloudy day (distribution is not applicable)
      // - true: "No Analysis" issue message shown (analysis in progress / cannot be accessed)
      // - false: charts are available (no issue message)
      'error - distribution analysis in progress': event.day.isCloudly
        ? undefined
        : isPreparingDistribution
          ? undefined
          : hasAnyDistributionDataForDay
            ? false
            : true,
      'date selection place': this.nextServicesMapViewedDateSelectionPlace ?? undefined,
      source: event.day.sourceType === 0 ? 'Sentinel' : 'Planet',
      'map type': this.selectedMapType?.labelAbreviation,
      'map selection place': this.nextServicesMapViewedMapSelectionPlace ?? undefined,
      'plantation id': this.plantationService.selectedPlantation.id,
      'plantation name': this.plantationService.selectedPlantation.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
      'has premium plan': this.getHasAnyPremiumPlan(),
      // Risk map only: include the property only when Risk is selected.
      // This keeps the event schema clean for other map types (NDVI/NDMI/Inspection).
      'problem spot':
        this.selectedMapType?.key === 'risk' ? (event.problematicSpots ?? []).length > 0 : undefined,
    });

    // Mixpanel: clear fallback marker once a matching event is emitted.
    if (this.noAnalysisIssueFallbackForDate === currentDate) {
      if (this.noAnalysisIssueFallbackTimer) {
        clearTimeout(this.noAnalysisIssueFallbackTimer);
        this.noAnalysisIssueFallbackTimer = null;
      }
      this.noAnalysisIssueFallbackForDate = null;
    }

    // Action-based views (fullscreen/map-type/notes/etc.) can trigger an immediate re-render/refresh
    // that emits another Services Map Viewed without action. Suppress that next non-action once.
    if (action) {
      this.suppressNextNonActionServicesMapViewed = true;
      this.suppressNextNonActionServicesMapViewedKey = nonActionContextKey;
    }

    // Mixpanel: action should apply only once for the next emitted view.
    this.nextServicesMapViewedAction = null;
    this.nextServicesMapViewedDateSelectionPlace = null;
    this.nextServicesMapViewedMapSelectionPlace = null;
  }

  // Mixpanel: date selection source for the next "Services Map Viewed" event only.
  onDateSelectionPlace(place: string): void {
    this.nextServicesMapViewedDateSelectionPlace = place;
  }

  // Mixpanel: map selection source for the next "Services Map Viewed" event only.
  onMapSelectionPlace(place: string): void {
    this.nextServicesMapViewedMapSelectionPlace = place;
  }

  // Mixpanel: log a Services Map Viewed event for a UI interaction without touching UI flow.
  private logServicesMapViewedAction(action: ServicesMapViewedAction): void {
    if (!this.lastSelectedCropHealthDayEvent?.day?.imageDate) {
      return;
    }
    this.trackServicesMapViewed(this.lastSelectedCropHealthDayEvent, action);
  }

  getImageBoundsFromPolygon(polygon: [number, number][]) {
    const lats = polygon.map((p) => p[1]);
    const lngs = polygon.map((p) => p[0]);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const bounds: [[number, number], [number, number], [number, number], [number, number]] = [
      [minLng, maxLat], // top-left
      [maxLng, maxLat], // top-right
      [maxLng, minLat], // bottom-right
      [minLng, minLat], // bottom-left
    ];

    // Ensure exactly 4 elements
    if (bounds.length !== 4) {
      throw new Error('getImageBoundsFromPolygon must return exactly 4 coordinates.');
    }
    return bounds;
  }

  onZoomClick(eventParam: string) {
    if (eventParam === 'plus') {
      this.mapboxService.zoom(0.6);
    } else {
      this.mapboxService.zoom(-0.6);
    }
  }

  onClickFullScreen() {
    this.fullScreenMode = !this.fullScreenMode;
    this.fullScreenEvent.emit();
    this.logServicesMapViewedAction('Full screen change');
    setTimeout(() => {
      this.mapboxService.map?.resize();
    }, 300);
  }

  onNotesViewChanged(_show: boolean): void {
    // Ensure the toggle state is reflected in dedupe key (open + close should both log).
    this.trackServicesMapViewed(this.lastSelectedCropHealthDayEvent, 'Notes view change', _show, null);
  }

  cleanRasterLayers() {
    if (this.mapInstance?.getLayer(this.layerId)) {
      this.mapInstance?.removeLayer(this.layerId);
    }

    if (this.mapInstance?.getSource(this.sourceId)) {
      this.mapInstance?.removeSource(this.sourceId);
    }
  }

  /** Toggles batch note pins; only called from notes toggle / mode changes—not on every map zoom. */
  showNotesPins(show: boolean) {
    if (!show) {
      this.notePinLayer.hideBatch();
      return;
    }

    const items = this.buildNotePinBatchItems();
    this.notePinLayer.showBatch(items);
    this.notePinLayer.ensureStackOrder(this.layerId);
  }

  /** Draft pin while adding a note (symbol layer, separate from batch note pins). */
  addMarker(latlng: LngLat): void {
    this.notePinLayer.setDraft(latlng.lng, latlng.lat);
    this.notePinLayer.ensureStackOrder(this.layerId);
  }

  removeMarker() {
    this.notePinLayer.clearDraft();
  }

  /** Maps NoteViewModel list to pin coordinates (spot notes use problematic-spot centroid). */
  private buildNotePinBatchItems(): NotePinBatchItem[] {
    const items: NotePinBatchItem[] = [];
    for (const note of this.notes) {
      const coordinate = new CoordinateViewModel();
      if (!note.problematicSpotId) {
        coordinate.latitude = note.coordinate.latitude;
        coordinate.longitude = note.coordinate.longitude;
      } else if (note.problematicSpotCoordinates?.length > 0) {
        const lats = note.problematicSpotCoordinates.map((c) => c.latitude);
        const lngs = note.problematicSpotCoordinates.map((c) => c.longitude);
        coordinate.latitude = lats.reduce((a, b) => a + b, 0) / lats.length;
        coordinate.longitude = lngs.reduce((a, b) => a + b, 0) / lngs.length;
      }

      if (
        coordinate &&
        !Number.isNaN(coordinate.longitude) &&
        !Number.isNaN(coordinate.latitude)
      ) {
        items.push({
          noteId: note.id,
          lng: coordinate.longitude,
          lat: coordinate.latitude,
          isProblematicSpot: !!note.problematicSpotId,
        });
      }
    }
    return items;
  }

  private fitCameraToBoundaries(boundaries: number[][]): void {
    const usable: number[][] = [];
    for (const point of boundaries) {
      if (Array.isArray(point) && Number.isFinite(point[0]) && Number.isFinite(point[1])) {
        usable.push(point);
      }
    }
    if (usable.length === 0) {
      return;
    }

    const lngs = usable.map((point) => point[0]);
    const lats = usable.map((point) => point[1]);
    this.mapInstance?.fitBounds([
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ] as LngLatBoundsLike);
  }
}
